import { useFocusRing } from '@react-aria/focus';
import { useHover, usePress } from '@react-aria/interactions';
import { mergeProps, useId } from '@react-aria/utils';
import cn from 'classnames';
import React, { forwardRef, memo, useContext } from 'react';
import { Box } from '../box';
import { Typography } from '../typography';
import styles from './styles.module.css';
import { TabsContext } from './Tabs';
export const TabBase = memo(forwardRef(function TabBase({ disabled, size = 'm', variant, hovered, focused, selected, pressed, className, children, value, onPress, ...rest }, ref) {
    const isSegments = variant === 'segments';
    return (React.createElement(Box, { pl: "s", pr: "s", alignItems: "center", alignContent: "center", flex: isSegments ? 1 : undefined, justifyContent: isSegments ? 'center' : undefined, className: cn(className, styles.tab, styles[size], {
            [styles.disabled]: disabled,
            [styles.hovered]: hovered,
            [styles.pressed]: pressed,
            [styles.selected]: selected,
            [styles.focused]: focused,
            [styles.segment]: isSegments,
        }), ref: ref, ...rest },
        React.createElement(Typography.Icon, { Component: "span", variant: isSegments || size === 's' ? 'body2' : 'body1', thickness: isSegments ? 'regular' : size === 's' ? 'medium' : 'bold' }, children)));
}));
export function useTabItem(props) {
    const tabsContext = useContext(TabsContext);
    if (!tabsContext) {
        throw new Error('Tab should be wrapped into Tabs');
    }
    const { disabled, tabIndex = 0, value, onPress } = props;
    const tabValue = useId(value);
    const { hoverProps, isHovered } = useHover({ isDisabled: disabled });
    const selected = tabsContext.value === tabValue;
    const { pressProps, isPressed } = usePress({
        onPress: e => {
            var _a;
            (_a = tabsContext.onChange) === null || _a === void 0 ? void 0 : _a.call(tabsContext, tabValue, e);
            onPress === null || onPress === void 0 ? void 0 : onPress(e);
        },
        isDisabled: disabled,
    });
    const { isFocusVisible, focusProps } = useFocusRing({ within: true });
    return mergeProps(props, {
        value: tabValue,
        hovered: isHovered,
        focused: isFocusVisible,
        pressed: isPressed,
        selected,
        size: tabsContext.size,
        variant: tabsContext.variant,
    }, pressProps, hoverProps, focusProps, {
        'aria-selected': selected,
        'aria-disabled': disabled || undefined,
        tabIndex: disabled ? undefined : tabIndex,
        role: 'tab',
    });
}
export const Tab = memo(forwardRef(function Tab(props, ref) {
    const tabProps = useTabItem(props);
    return React.createElement(TabBase, { ...tabProps, ref: ref });
}));
